import {
  DateTimeValue,
  DurationValue,
  Entity,
  GenericFieldValue,
  ListValue,
  LookupValue,
  NumericValue,
  OptionsetValue,
  StringValue,
} from '../data-objects'
import {
  BaseValidationDefinition,
  DefinitionUtils,
  EntityModel,
  FieldDefinition,
  FormGroupDefinition,
  FormModel,
  NumericFieldDefinition,
  ValidationDefinition,
} from '../definitions'
import { FieldSelectOptions, findNode, FormNode } from '../form-structure'
import {
  getConversionUM,
  getNameAndAlias,
  readEntityModelByName,
  tryConvert,
  tryConvertDecimalInfo,
  tryRevert,
} from '../utils'
import { CustomRowValidations } from './custom-row-validations'

export class EntityContext {
  private readonly entity: Entity
  private readonly additionalEnties: Record<string, Entity>
  private readonly entityAliases: Record<string, string>
  private readonly metadata: Record<string, EntityModel>
  private readonly formModel?: FormModel
  private readonly formNode: FormNode

  public readonly customValidaitons: BaseValidationDefinition[]
  public readonly fields: Record<string, GenericFieldValue>

  public get entityName() {
    return this.entity.entityName
  }

  public get id() {
    return this.entity.id
  }

  public get internalId() {
    return this.entity.internalId
  }

  public get IsPersisted() {
    return this.entity.isPersisted
  }

  public get IsInCreation() {
    return this.entity.isInCreation
  }

  public get ParentId() {
    return this.entity.parentId
  }

  public get CanUpdate() {
    return this.entity.canUpdate
  }

  public get CanActivate() {
    return this.entity.canActivate
  }

  public get CanDeactivate() {
    return this.entity.canDeactivate
  }

  public get CanDelete() {
    return this.entity.canDelete
  }

  constructor(
    entity: Entity,
    formNode: FormNode,
    additionalEntities?: Entity[],
    formModel?: FormModel,
    customValidations?: BaseValidationDefinition[],
  ) {
    this.entity = entity
    this.customValidaitons = customValidations ?? []
    this.additionalEnties = {}
    this.metadata = {}
    this.formModel = formModel
    this.formNode = formNode

    this.entityAliases = {
      ['FE']: 'FORM_ENTITY',
      ['']: this.entity.entityName,
    }

    if (formModel?.linkEntities) {
      formModel.linkEntities.forEach((lk) => {
        this.entityAliases[lk.as] = lk.entity
      })
    }

    const addEntities = additionalEntities ?? []
    addEntities.forEach((e) => {
      this.additionalEnties[e.entityName] = e
    })

    this.setupMetadata()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.fields = new Proxy(this, this.getFieldHandler()) as any
  }

  public IsEmpty(fieldName: string): boolean {
    const field = this.getField(fieldName)
    return field === null || field === undefined || !!field?.isEmpty()
  }

  public AreAllEmpty(fields: string): boolean {
    const splitted = fields.split(',')
    let result = true
    splitted.forEach((field) => {
      result = result && this.IsEmpty(field)
    })
    return result
  }

  public Str(fieldName: string): string | null {
    const field = this.getField(fieldName) as StringValue
    return field ? (field.value as string) : null
  }

  public StrLower(fieldName: string): string | null {
    const field = this.getField(fieldName) as StringValue
    return field ? (field.value as string).toLowerCase() : null
  }

  public StrUpper(fieldName: string): string | null {
    const field = this.getField(fieldName) as StringValue
    return field ? (field.value as string).toUpperCase() : null
  }

  public StrInLower(fieldName: string, valueList: string): boolean {
    let res = false
    const field = this.getField(fieldName) as StringValue

    if (field != null) {
      const strValue = (field.value as string).trim()
      const valuesList = valueList.split(',')

      for (let i = 0; i < valuesList.length && !res; i++) {
        const item = valuesList[i]
        res = strValue.toLowerCase() == item.trim().toLowerCase()
      }
    }

    return res
  }

  public StrContains(fieldName: string, strContained: string): boolean {
    let result = false

    const field = this.getField(fieldName) as StringValue | null

    if (field?.value != null) {
      const strValue = (field.value as string).trim()
      result = strValue.includes(strContained)
    }

    return result
  }

  public Length(fieldName: string): number | null {
    const field = this.getField(fieldName)
    if (field instanceof StringValue && typeof field.value === 'string') {
      return field.value.length
    }
    return null
  }

  public GetPrecision(v: number): number {
    const str = v.toString()
    const dotIndex = str.indexOf('.')
    return dotIndex === -1 ? 0 : str.length - dotIndex - 1
  }

  public MatchToStep(fieldName: string, step: number): boolean {
    const value = this.Num(fieldName)
    if (value == null) return true

    const precision = Math.min(15, Math.max(this.GetPrecision(value), this.GetPrecision(step)))
    const rest = value % step
    const roundedRest = Math.round(rest * Math.pow(10, precision)) / Math.pow(10, precision)
    return roundedRest === 0 || Math.abs(roundedRest) === step
  }

  public CheckRange(fieldName: string, min: number, max: number): boolean {
    const valueToCheck = this.ConvertedNum(fieldName)

    const resolved = getNameAndAlias(fieldName)
    const entityName = this.entityAliases[resolved.alias]
    const entityModel = this.metadata[entityName]
    const fieldDef = entityModel.getFieldDefinition(resolved.name) as NumericFieldDefinition
    const convertedDecimalInfo = tryConvertDecimalInfo(
      fieldDef.decimalInfo,
      fieldDef.um,
      entityName,
      resolved.name,
      fieldDef.step,
    )
    const convertedMin = tryConvert(min ?? Number.NEGATIVE_INFINITY, fieldDef.um, fieldDef.entityName, resolved.name)
    const convertedMax = tryConvert(max ?? Number.POSITIVE_INFINITY, fieldDef.um, fieldDef.entityName, resolved.name)
    const minToUse = parseFloat(convertedMin.toFixed(convertedDecimalInfo?.decimalLimit ?? 0))
    const maxToUse = parseFloat(convertedMax.toFixed(convertedDecimalInfo?.decimalLimit ?? 0))

    return valueToCheck != null && (valueToCheck < minToUse || valueToCheck > maxToUse)
  }

  private ConvertedNum(fieldName: string): number | null {
    const field = this.getField(fieldName) as NumericValue
    if (field?.value == null || field?.value == undefined) {
      return null
    }

    const resolved = getNameAndAlias(fieldName)
    const entityName = this.entityAliases[resolved.alias]
    const entityModel = this.metadata[entityName]
    const fieldDef = entityModel.getFieldDefinition(resolved.name) as NumericFieldDefinition
    const convertedDecimalInfo = tryConvertDecimalInfo(fieldDef.decimalInfo, fieldDef.um, entityName, resolved.name)
    const rounded = parseFloat(
      convertedDecimalInfo?.decimalLimit
        ? field.value.toFixed(convertedDecimalInfo?.decimalLimit)
        : field.value.toString(),
    )
    return rounded
  }

  public Num(fieldName: string): number | null {
    const field = this.getField(fieldName) as NumericValue
    if (field) {
      // to ensure that whe are accessing the correct entity
      // first resolve the field name to extract an alias
      // then use the alias to recover the entity name to use
      const resolved = getNameAndAlias(fieldName)
      const entityName = this.entityAliases[resolved.alias]
      const entityModel = this.metadata[entityName]
      const fieldDef = entityModel.getFieldDefinition(resolved.name)
      return tryRevert(field.value as number, fieldDef.um, entityName, resolved.name)
    }
    return null
  }

  public TotalMinutes(fieldName: string): number | null {
    const field = this.getField(fieldName) as DurationValue
    if (field) {
      return field.value?.as('minutes') ?? null
    }
    return null
  }

  public OpSet(fieldName: string): string | null {
    const field = this.getField(fieldName) as OptionsetValue
    return field ? (field.value as string) : null
  }

  public Date(fieldName: string) {
    const field = this.getField(fieldName) as DateTimeValue
    return field ? field.value : null
  }

  public IsValidDate(fieldName: string) {
    const field = this.getField(fieldName) as DateTimeValue
    return field?.value?.isValid ?? false
  }

  public OpSetContains(attributeName: string, value: string): boolean {
    const fieldValue = this.getField(attributeName) as OptionsetValue
    if (!fieldValue || !fieldValue.value || fieldValue.value == '') {
      return false
    }

    const fieldValues: string[] = fieldValue.value.split('|')
    let result = false
    fieldValues.forEach((item) => {
      if (item == value) {
        result = true
      }
    })

    return result
  }

  public OpSetIn(fieldName: string, valueList: string): boolean {
    const valuesOpt = valueList.split(',')

    const value = this.getField(fieldName) as OptionsetValue
    if (!value || !value.value || value.value == '') {
      return false
    }

    let result = true
    const values: string[] = value.value.split('|')
    values.forEach((item) => {
      result = result && valuesOpt.indexOf(item) != -1
    })
    return result
  }

  public ListIsUnique(fieldName: string, uniqueFields: string): boolean {
    const list = this.getField(fieldName) as ListValue
    return list ? list.IsUnique(uniqueFields) : false
  }

  public ListHasValue(
    fieldName: string,
    columnName: string,
    columnValue: string,
    opSetName?: string,
    opSetValue?: string,
  ): boolean {
    const list = this.getField(fieldName) as ListValue
    return (
      list.value?.some(
        (x) => x.fields[columnName]?.value == columnValue && (!opSetName || x.fields[opSetName]?.value == opSetValue),
      ) == true
    )
  }

  public IsPointing(fieldName: string, entityName: string): boolean {
    const value = this.getField(fieldName) as LookupValue
    if (!value) return false
    return value.entityName === entityName
  }

  public LkFields(fieldName: string): EntityContext {
    const value = this.getField(fieldName) as LookupValue
    const fakeEntity = { id: value?.value, entityName: value?.entityName, fields: value?.fields ?? {} } as Entity
    const context = new EntityContext(fakeEntity, {} as FormNode, [])
    return context
  }

  public addRow(fieldName: string): Entity {
    let list = this.getField(fieldName) as ListValue
    let fields: FieldDefinition[] = []
    if (fieldName.includes('.')) {
      const resolved = getNameAndAlias(fieldName)
      const entityName = this.entityAliases[resolved.alias]
      const entity = this.additionalEnties[entityName]
      // eslint-disable-next-line prefer-destructuring
      fields = this.metadata[entityName].getFieldDefinition(resolved.name).fields
      if (!list) {
        entity.fields[resolved.name] = new ListValue()
        list = entity.fields[resolved.name] as ListValue
        list.value = []
      }
    } else {
      // eslint-disable-next-line prefer-destructuring
      fields = this.metadata[this.entityName].getFieldDefinition(fieldName).fields
      if (!list) {
        this.entity.fields[fieldName] = new ListValue()
        list = this.entity.fields[fieldName] as ListValue
        list.value = []
      }
    }

    const row: Entity = {
      entityName: this.entity.entityName,
      id: crypto.randomUUID(),
      isPersisted: false,
      parentId: this.entity.id,
      canUpdate: this.entity.canUpdate,
      canDeactivate: this.entity.canDeactivate,
      canActivate: this.entity.canActivate,
      canDelete: this.entity.canDelete,
      fields: {},
      isInCreation: false,
    }

    fields.forEach((f) => {
      row.fields[f.name] = null
    })

    list.value?.push(row)
    return row
  }

  public getRows(fieldName: string): EntityContext[] {
    const res: EntityContext[] = []
    const list = this.getField(fieldName) as ListValue
    if (list) {
      list.value?.forEach((r) => {
        const row = this.getRow(fieldName, r.id) as EntityContext
        res.push(row)
      })
    }

    return res
  }

  public getRow(fieldName: string, rowId: string): EntityContext | null {
    const list = this.getField(fieldName) as ListValue
    if (list) {
      const row = list.value?.find((r) => r.id === rowId) as Entity
      const customRowValidations: CustomRowValidations = new CustomRowValidations(
        this.customValidaitons,
        fieldName,
        rowId,
      )
      const entityContext = new EntityContext(row, this.formNode, undefined, undefined, customRowValidations)
      return entityContext
    }
    return null
  }

  public addValidation(field: string, msgCode: string, onList?: string, params?: string[]): void {
    const validation: ValidationDefinition = {
      field,
      condition: 'true',
      msgCode,
      validationType: 'Expression',
      onList,
      msgParameters: params ? params.join(',') : undefined,
    }
    this.customValidaitons.push(validation)
  }

  public unbox(): { entity: Entity; additionalEntities: Entity[] } {
    return {
      entity: this.entity,
      additionalEntities: Object.keys(this.additionalEnties).map((k) => this.additionalEnties[k]),
    }
  }

  public removeOptOptions(fieldName: string, toRemove: string[]): void {
    const node = findNode(this.formNode, fieldName)
    if (!node) {
      throw new Error(`[removeOptOptions] - node with name ${fieldName} not found`)
    }

    const options = node.value.selectOptions as FieldSelectOptions
    options.remove = toRemove
  }

  public keepOptOptions(fieldName: string, toKeep: string[]): void {
    const node = findNode(this.formNode, fieldName)
    if (!node) {
      throw new Error(`[keepOptOptions] - node with name ${fieldName} not found`)
    }

    const options = node.value.selectOptions as FieldSelectOptions
    options.keep = toKeep
  }

  /**
   * These function should be used when setting a numeric value from code
   * and it will take care of converting the value to the correct format.
   * This should be the only way of setting a new value in a numeric,
   * the developer should never make any assumptions on the UM or conversion
   */
  public setNumeric(fieldName: string, value: number) {
    // to ensure that whe are accessing the correct entity
    // first resolve the field name to extract an alias
    // then use the alias to recover the entity name to use
    const resolved = getNameAndAlias(fieldName)
    const entityName = this.entityAliases[resolved.alias]
    const entityModel = this.metadata[entityName]
    const fieldDef = entityModel.getFieldDefinition(resolved.name)
    const converted = tryConvert(value, fieldDef.um, entityName, resolved.name)
    const c = getConversionUM(fieldDef.um, entityName, resolved.name)
    const field = new NumericValue(converted, c ?? fieldDef.um)
    this.entity.fields[fieldName] = field
  }

  /**
   * Return all the field that are child of the given group
   * in a flat list, meaning that if the group contains other
   * groups inside those are navigated recursively and all
   * fields are found
   */
  public getFieldsInsideGroup(groupName: string): string[] {
    const groupDef = this.formModel?.getFormDefinition(groupName) as FormGroupDefinition
    let fields: string[] = []
    if (groupDef.layout) {
      groupDef.layout.forEach((l) => {
        switch (l.type) {
          case 'Group':
          case 'Tab': {
            const innerFields = this.getFieldsInsideGroup(l.name)
            fields = [...fields, ...innerFields]
            break
          }
          case 'Field':
          case 'MetadataSelector':
          case 'MultiSelector': {
            fields.push(l.name)
            break
          }
          default:
            break
        }
      })
    }
    return fields
  }

  public getAliasFromEntityId(id: string): string | undefined {
    const entities = [this.entity, ...Object.values(this.additionalEnties)]
    const entity = entities.find((e) => e.id === id)

    if (!entity) return

    const keys = Object.keys(this.entityAliases)
    const alias = keys.find((k) => this.entityAliases[k] === entity.entityName)
    return alias as string
  }

  private getField(fieldName: string): GenericFieldValue {
    let field = this.entity?.fields[fieldName] as GenericFieldValue
    if (fieldName.includes('.')) {
      const resolved = getNameAndAlias(fieldName)
      const entityName = this.entityAliases[resolved.alias]
      const entity = this.additionalEnties[entityName]
      field = entity?.fields[resolved.name]
    }
    return field
  }

  private getFieldHandler() {
    const fieldHanlder: ProxyHandler<EntityContext> = {
      get(target, propKey, _reciever) {
        return target.getField(propKey as string)
      },
      set(target, propKey, newValue, _reciever) {
        const fieldName = propKey as string
        if (fieldName.includes('.')) {
          const resolved = getNameAndAlias(fieldName)
          const entityName = target.entityAliases[resolved.alias]
          const entity = target.additionalEnties[entityName]
          entity.fields[resolved.name] = newValue
        } else {
          target.entity.fields[fieldName] = newValue
        }
        return true
      },
    }
    return fieldHanlder
  }

  private setupMetadata() {
    this.metadata[this.entity.entityName] = readEntityModelByName(this.entity.entityName) as EntityModel
    const addEntities = Object.keys(this.additionalEnties).map((k) => this.additionalEnties[k])
    addEntities.forEach((e) => {
      if (e.entityName === 'FORM_ENTITY' && this.formModel) {
        const mapped = DefinitionUtils.mapFieldDefinitions(this.formModel.fields)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const model: any = { fields: mapped, name: e.entityName }
        this.metadata[e.entityName] = new EntityModel(model)
      } else {
        const model = readEntityModelByName(e.entityName) as EntityModel
        this.metadata[e.entityName] = model
      }
    })
  }
}
